import { MutationOptions, keepPreviousData, useMutation, useQuery } from "@tanstack/vue-query"

import { QueryOptions, ServiceReturnType } from "../../ui/utils/query.utils"
import { KycService } from "./services/kyc.service"

export type KycGetQuestionnaireProgress = ServiceReturnType<typeof KycService.getQuestionnaireProgress>
export const KYC_QUESTIONNAIRE_PROGRESS_QUERY_KEY = "KycService.getQuestionnaireProgress"
export function useFetchKycLobby<T extends KycGetQuestionnaireProgress>(
  params: { questionnaireId: string; phase?: Ref<number> },
  options?: QueryOptions<KycGetQuestionnaireProgress>
) {
  return useQuery<T>({
    queryKey: [KYC_QUESTIONNAIRE_PROGRESS_QUERY_KEY, params.phase],
    queryFn: ({ signal }) =>
      KycService.getQuestionnaireProgress(
        jsonClean({ questionnaire: params?.questionnaireId, phase: params.phase?.value }),
        { signal }
      ),
    staleTime: 0,
    placeholderData: keepPreviousData,
    ...(options as any),
    enabled: computed(() => !!params.phase?.value && toRef((options as any)?.enabled as boolean).value),
  })
}

export type KycAnswers = ServiceReturnType<typeof KycService.getQuestionnaireAnswers>

export function useFetchKycAnswersQuery<T extends KycAnswers = KycAnswers>(
  params?: KycFetchKycAnswersParams,
  options?: QueryOptions<T>
) {
  const { questions } = params || {}
  return useQuery<T>({
    queryKey: ["KycService.getQuestionnaireAnswers", questions],
    queryFn: ({ signal }) => KycService.getQuestionnaireAnswers({ signal, params: jsonClean({ questions }) }),
    // queryFn: () => {
    //   return {
    //     id: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //     firstName: "תומר משתמש בדיקות",
    //     lastName: "דנן",
    //     email: null,
    //     phoneNumber: "+972525268950",
    //     phoneNumberCountryCode: "IL",
    //     dateOfBirth: "1997-02-16",
    //     genderPronoun: "M",
    //     idNumber: "207992211",
    //     dateOfIssue: null,
    //     gender: "M",
    //     employeeStatus: "employed",
    //     jobOccupation: "programmer",
    //     publicPosition: null,
    //     healthChange: false,
    //     smoking: true,
    //     otherBeneficiaries: null,
    //     refusedServices: null,
    //     familyStatus: "married",
    //     childrenAmount: 3,
    //     financialSupport: null,
    //     investmentsBackground: null,
    //     investmentsPeriod: 3,
    //     responseStrategy: 3,
    //     riskToleranceScenario: 3,
    //     investmentRisk: null,
    //     investmentRiskLevel: null,
    //     pensionRisk: 71.9352987030695,
    //     pensionRiskLevel: 5,
    //     verificationDate: 0,
    //     investmentsPurpose: null,
    //     investmentsAmount: null,
    //     investmentsEconomic: null,
    //     investmentsLost: null,
    //     jobLost: null,
    //     age: null,
    //     jobPreference: null,
    //     anotherCitizen: null,
    //     usaCitizen: null,
    //     publicJob: null,
    //     moneySource: null,
    //     beneficiaryDeclared: null,
    //     beneficiaryDeclaredApproved: null,
    //     city: "978",
    //     street: "7588",
    //     houseNumber: "87",
    //     postalCode: "12334",
    //     primaryBeneficiary: "choose_beneficiaries",
    //     contingentBeneficiary: "equivalent_other_beneficiaries",
    //     signedContract: null,
    //     significantHealthEvent: null,
    //     sectionFourteen: false,
    //     pensionDepositPercentage: 6,
    //     updatePensionDepositPercentage: true,
    //     pensionProvisionAmount: 24400,
    //     educationReportedSalary: 1571.2,
    //     additionalAllowanceOrPension: true,
    //     additionalAllowanceOrPensionAmount: 5000,
    //     aroyaPersonId: 107800,
    //     occupationLifestyleRisks: true,
    //     occupationLifestyleRisksExplanation: "hey :(",
    //     agreedPensionRequiredTerms: true,
    //     extendedForeignStay: true,
    //     extendedForeignStayCountry: "CD",
    //     extremeHobbies: ["boxing"],
    //     airWorker: null,
    //     children: [
    //       {
    //         id: 81,
    //         userId: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //         firstName: "123",
    //         lastName: "גדש",
    //         gender: null,
    //         dateOfBirth: -26733196800,
    //         idNumber: null,
    //         disability: false,
    //         disabilityRecognitionDate: null,
    //       },
    //       {
    //         id: 74,
    //         userId: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //         firstName: "12344",
    //         lastName: "דנן",
    //         gender: null,
    //         dateOfBirth: -26730518400,
    //         idNumber: "230933822",
    //         disability: false,
    //         disabilityRecognitionDate: null,
    //       },
    //       {
    //         id: 82,
    //         userId: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //         firstName: "asd",
    //         lastName: "3321",
    //         gender: null,
    //         dateOfBirth: -27080352000,
    //         idNumber: null,
    //         disability: false,
    //         disabilityRecognitionDate: null,
    //       },
    //     ],
    //     spouse: {
    //       id: 72,
    //       userId: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //       firstName: "vvvvvvvvv",
    //       lastName: "1111",
    //       gender: "F",
    //       dateOfBirth: null,
    //       idNumber: "207992215",
    //       phoneNumber: "+972525268950",
    //       phoneNumberCountryCode: "IL",
    //     },
    //     employer: {
    //       id: 25,
    //       userId: "3de4928c-c2c4-4a37-8eab-6e6b2394c749",
    //       employerName: null,
    //       employerIdentificationNumber: null,
    //       employerAddress: null,
    //       employerContactEmail: "batshel@eyal.golan",
    //       employerContactName: "הבת של אייל גולן",
    //       employerContactPhone: "+972525268950",
    //     },
    //     beneficiaries: [],
    //   } as T
    // },
    staleTime: 0,
    ...options,
  })
}

export function useFetchKycAnswersMutation(
  options?: MutationOptions<KycAnswers, Error, KycFetchKycAnswersParams>
) {
  return useMutation({
    mutationKey: ["KycService.getQuestionnaireAnswers"],
    mutationFn: (params: KycFetchKycAnswersParams) =>
      KycService.getQuestionnaireAnswers({ params: jsonClean(params) }),
    ...options,
  })
}

export function useGetDocumentTypes(params?: { group?: string; subGroups?: string[] }) {
  const { group, subGroups } = params || {}
  return useQuery({
    queryKey: ["DocumentsService.getTypes", group, subGroups],
    queryFn: ({ signal }) => {
      return DocumentsService.getTypes({ signal, params: jsonClean({ group }) })
    },
    enabled: Boolean(group),
  })
}

export function useUserUploadedFiles(params?: { group?: string; subGroups?: string[] }) {
  const { group, subGroups } = params || {}
  return useQuery({
    queryKey: ["DocumentsService.getUserDocuments", group, subGroups],
    queryFn: ({ signal }) => {
      return DocumentsService.getUserDocuments({ signal, params: jsonClean({ group }) })
    },
    enabled: Boolean(group),
  })
}

export type GetDetailsSecuredUrl = Awaited<ReturnType<typeof DocumentsService.downloadAndCreateBlob>>

export function useGetDetailsSecuredUrl(
  params?: { currentUserDocuments: any },
  options?: QueryOptions<GetDetailsSecuredUrl[]>
) {
  return useQuery<GetDetailsSecuredUrl[]>({
    queryKey: ["SecureDetails.getUrls", params?.currentUserDocuments],
    queryFn: () => {
      if (!params?.currentUserDocuments?.value.length) return []
      const documents = params?.currentUserDocuments.value[0].details
      return Promise.all(documents.map((detail: any) => DocumentsService.downloadAndCreateBlob(detail.id)))
    },
    staleTime: 0,
    ...options,
  })
}
